import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Icon, cn } from "@nexus/ui";

import { AdminKpiStrip } from "@/features/admin/components/layout/AdminKpiStrip";
import type { AdminOrdersSummary } from "@/features/marketplace/types/marketplace.types";
import { AdminOrdersDeadlineWidget } from "./AdminOrdersDeadlineWidget";
import { AdminOrdersMovementChart } from "./AdminOrdersMovementChart";
import { AdminOrdersPartnerWidget } from "./AdminOrdersPartnerWidget";
import { AdminOrdersPipelineWidget } from "./AdminOrdersPipelineWidget";
import type {
  AdminOrdersOverviewAction,
  AdminOrdersOverviewActiveFilters,
} from "./admin-orders-overview";

interface AdminOrdersOverviewProps {
  summary?: AdminOrdersSummary;
  isLoading: boolean;
  isStale: boolean;
  isError: boolean;
  isGlobalScope: boolean;
  activeFilters: AdminOrdersOverviewActiveFilters;
  onAction: (action: AdminOrdersOverviewAction) => void;
  onRetry: () => void;
}

export function AdminOrdersOverview({
  summary,
  isLoading,
  isStale,
  isError,
  isGlobalScope,
  activeFilters,
  onAction,
  onRetry,
}: AdminOrdersOverviewProps) {
  const { t, i18n } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const contentId = useId();
  const formatter = new Intl.NumberFormat(
    i18n.resolvedLanguage ?? i18n.language,
  );
  const movementFrom = summary?.movement[0]?.date;
  const movementTo = summary?.movement.at(-1)?.date;
  const isDeliveredRecentActive =
    activeFilters.fulfillmentGroup === "history" &&
    movementFrom != null &&
    movementTo != null &&
    activeFilters.deliveredFrom === movementFrom &&
    activeFilters.deliveredTo === movementTo;
  const activeMovementSeries =
    activeFilters.createdFrom === activeFilters.createdTo &&
    activeFilters.createdFrom
      ? "created"
      : activeFilters.deliveredFrom === activeFilters.deliveredTo &&
          activeFilters.deliveredFrom
        ? "delivered"
        : undefined;
  const activeMovementDate =
    activeMovementSeries === "created"
      ? activeFilters.createdFrom
      : activeMovementSeries === "delivered"
        ? activeFilters.deliveredFrom
        : undefined;

  const metrics = [
    {
      id: "open-orders",
      label: t(
        "marketplace.ordersDashboard.overview.openOrders",
        "Open orders",
      ),
      value: summary ? formatter.format(summary.open) : null,
      isMuted: summary?.open === 0,
      onFilter: summary
        ? () =>
            onAction({
              kind: "fulfillmentGroup",
              value: "active",
            })
        : undefined,
      isFilterActive: activeFilters.fulfillmentGroup === "active",
    },
    {
      id: "open-scripts",
      label: t(
        "marketplace.ordersDashboard.overview.openScripts",
        "Open scripts",
      ),
      value: summary ? formatter.format(summary.openScripts) : null,
      isMuted: summary?.openScripts === 0,
      onFilter: summary
        ? () =>
            onAction({
              kind: "fulfillmentGroup",
              value: "active",
            })
        : undefined,
      isFilterActive: activeFilters.fulfillmentGroup === "active",
    },
    {
      id: "attention",
      label: t(
        "marketplace.ordersDashboard.overview.attention",
        "Needs attention",
      ),
      value: summary ? formatter.format(summary.attention) : null,
      isMuted: summary?.attention === 0,
      tone: "error" as const,
      onFilter: summary ? () => onAction({ kind: "attention" }) : undefined,
      isFilterActive: activeFilters.attentionOnly,
    },
    {
      id: "overdue",
      label: t("marketplace.ordersDashboard.overview.overdue", "Overdue"),
      value: summary ? formatter.format(summary.overdue) : null,
      isMuted: summary?.overdue === 0,
      tone: "warning" as const,
      onFilter: summary
        ? () => onAction({ kind: "deadline", value: "overdue" })
        : undefined,
      isFilterActive: activeFilters.deadlineBucket === "overdue",
    },
    {
      id: "delivered-recent",
      label: t(
        "marketplace.ordersDashboard.overview.deliveredRecent",
        "Delivered · 30d",
      ),
      value: summary
        ? formatter.format(summary.deliveredLast30Days)
        : null,
      isMuted: summary?.deliveredLast30Days === 0,
      onFilter:
        movementFrom && movementTo
          ? () =>
              onAction({
                kind: "deliveredRecent",
                from: movementFrom,
                to: movementTo,
              })
          : undefined,
      isFilterActive: isDeliveredRecentActive,
    },
  ];

  return (
    <section
      aria-labelledby="admin-orders-overview-title"
      className="overflow-hidden rounded-md border border-border bg-card"
    >
      <header className="flex min-h-11 items-center gap-3 border-b border-border px-4 py-2">
        <button
          type="button"
          aria-expanded={!isCollapsed}
          aria-controls={contentId}
          onClick={() => setIsCollapsed((current) => !current)}
          className="flex min-h-11 cursor-pointer items-center gap-2 rounded-sm outline-none transition-colors duration-150 hover:text-primary focus-visible:ring-1 focus-visible:ring-ring motion-reduce:transition-none"
        >
          <Icon
            name="chevron-down"
            size="sm"
            color="inherit"
            aria-hidden
            className={cn(
              "transition-transform duration-150 motion-reduce:transition-none",
              isCollapsed && "-rotate-90",
            )}
          />
          <h1
            id="admin-orders-overview-title"
            className="font-mono text-[10px] uppercase tracking-label text-muted-foreground"
          >
            {t(
              isGlobalScope
                ? "marketplace.ordersDashboard.overview.title"
                : "marketplace.ordersDashboard.overview.titleScoped",
              isGlobalScope ? "Global order health" : "Order health",
            )}
          </h1>
        </button>
        {isCollapsed ? (
          <span className="ml-auto text-caption tabular-nums text-muted-foreground">
            {t(
              "marketplace.ordersDashboard.overview.collapsedSummary",
              "{{open}} open · {{attention}} attention",
              {
                open: summary?.open ?? 0,
                attention: summary?.attention ?? 0,
              },
            )}
          </span>
        ) : null}
      </header>

      {isCollapsed ? null : (
        <div id={contentId}>
          <div className="border-b border-border px-4 py-3">
            <AdminKpiStrip
              metrics={metrics}
              // Labels are known before the summary lands — dash values instead
              // of swapping the strip for unlabeled bars.
              isLoading={false}
              isStale={isStale || isLoading}
              isError={isError}
              onRetry={onRetry}
            />
          </div>

          {summary ? (
            <div className="grid divide-y divide-border md:grid-cols-2 md:divide-y-0 xl:grid-cols-[minmax(0,3fr)_minmax(22rem,2fr)] [&>*]:border-border md:[&>*:nth-child(even)]:border-l md:[&>*:nth-child(n+3)]:border-t">
              <AdminOrdersPipelineWidget
                counts={summary.openScriptsByStatus}
                activeStatuses={activeFilters.statuses}
                onSelect={(value) =>
                  onAction({ kind: "status", value })
                }
              />
              <AdminOrdersDeadlineWidget
                counts={summary.deadlineBuckets}
                activeBucket={activeFilters.deadlineBucket}
                onSelect={(value) =>
                  onAction({ kind: "deadline", value })
                }
              />
              <AdminOrdersPartnerWidget
                partners={summary.topPartners}
                activeVendorIds={activeFilters.vendorIds}
                onSelect={(value) =>
                  onAction({ kind: "vendor", value })
                }
              />
              <AdminOrdersMovementChart
                points={summary.movement}
                activeSeries={activeMovementSeries}
                activeDate={activeMovementDate}
                onSelect={(series, date) =>
                  onAction({ kind: "movement", series, date })
                }
              />
            </div>
          ) : isError ? null : isLoading ? (
            <div
              className="min-h-48"
              aria-busy
              aria-label={t(
                "marketplace.ordersDashboard.overview.loadingWidgets",
                "Loading order health details",
              )}
            />
          ) : (
            <div className="flex min-h-48 items-center justify-center">
              <Button variant="outline" size="sm" onClick={onRetry}>
                {t("admin.layout.retry", "Try again")}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
